import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { message, Modal, Upload } from "antd";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import {
  Add_update_super_admins,
  currentUserProfileQuery,
  User_Account_Query,
  Upload_Document,
} from "../../services/userAPI";
import QPLogo from "../../Images/QPLogo.png";
import ClanCrewLogo from "../../Images/clan-crew-dashboard-logo.png";
import HeaderCloseIcon from "../../Images/header_redcircle_down.png";
import HeaderOpenIcon from "../../Images/header-circle-down.png";
import HeaderOpenW from "../../Images/expand_circle-down-white-color.png";
import EditIcon from "../../Images/editIcon2.png";
import LogoutIcon from "../../Images/logotIcon.png";
import DefaultProfile from "../../Images/profile-user.png";
import CreateClientImage from "../../Images/add-new-profile-photo.png";

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const LiteGrowthHeader = ({ setIsAuthenticated = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const imageRef = useRef(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [clientImage, setClientImage] = useState(null);
  const [uploadedFilename, setUploadedFilename] = useState(null);
  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [maskedPassword, setMaskedPassword] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    emailId: "",
    password: "",
    phoneNumber: "",
    imageUrl: "",
    roleId: "",
  });

  /* -------------------------------- queries -------------------------------- */
  const { data, refetch } = useQuery(currentUserProfileQuery, {
    skip: !!userData,
    fetchPolicy: "network-only",
  });
  const { data: accountData } = useQuery(User_Account_Query, {
    fetchPolicy: "network-only",
  });
  const [uploadDocument] = useMutation(Upload_Document);
  const [addUpdateSuperAdmin] = useMutation(Add_update_super_admins);

  useEffect(() => {
    if (data?.currentUserProfile) setUserData(data.currentUserProfile);
  }, [data]);

  /* ------------------------------ dropdown open ---------------------------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const getIcon = () => {
    if (isDropdownOpen) return HeaderOpenIcon;
    if (isHovered) return HeaderOpenW;
    return HeaderCloseIcon;
  };

  const truncateUserName = (name = "") => {
    const first = name.split(" ")[0];
    return first.length > 13 ? first.substring(0, 13) + "..." : first;
  };
  const truncateDesignation = (d = "") =>
    d.split(" ").length > 2 ? d.split(" ").slice(0, 2).join(" ") + "..." : d;

  /* --------------------------------- actions ------------------------------- */
  const logout = () => {
    localStorage.removeItem("flutter.accessToken");
    setIsAuthenticated(false);
    setUserData(null);
    window.location.href = "/login-page";
  };

  const handleEditClient = () => {
    const accountId = accountData?.currentUserAccount?.id;
    setIsDropdownOpen(false);
    if (!accountId) {
      message.error("Unable to find your client account");
      return;
    }
    navigate(`/edit-client-and-role/${accountId}`);
  };

  const handleEditClick = () => {
    setFormData({
      name: userData?.name || "",
      emailId: userData?.email || "",
      password: userData?.password || "",
      phoneNumber: userData?.phoneNumber || "",
      imageUrl: userData?.profilePicture || "",
      roleId: userData?.roleId || userData?.role?.id || "",
    });
    setMaskedPassword("*".repeat((userData?.password || "").length));
    setIsDropdownOpen(false);
    setIsEditModalVisible(true);
  };

  const handleCancel = () => {
    setIsEditModalVisible(false);
    setClientImage(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Enter users name.";
    if (!formData.emailId?.trim()) newErrors.emailId = "Enter email-id.";
    else if (!emailRegex.test(formData.emailId)) newErrors.emailId = "Please enter a valid email address.";
    if (!formData.imageUrl && !clientImage) newErrors.clientImage = "Upload image.";
    if (!formData.password) newErrors.password = "Enter password.";
    if (!formData.phoneNumber) newErrors.phoneNumber = "Enter contact number.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      message.error("Please fix the errors before submitting.");
      return;
    }
    try {
      const { data: updateData } = await addUpdateSuperAdmin({
        variables: {
          updatedUser: {
            userId: userData.id,
            firstName: formData.name,
            surname: "",
            email: formData.emailId,
            password: formData.password,
            contactNumber: formData.phoneNumber,
            profilePicture: uploadedFilename,
            userStatus: null,
            userType: null,
            roleId: formData.roleId || userData?.roleId || userData?.role?.id || "",
          },
        },
      });
      if (updateData?.addUpdateSuperAdmins?.success) {
        handleCancel();
        setUserData((prev) => ({
          ...prev,
          name: formData.name,
          email: formData.emailId,
          phoneNumber: formData.phoneNumber,
          profilePicture: updateData?.addUpdateSuperAdmins?.data?.profilePicture,
        }));
        await refetch();
        message.success("Profile updated successfully!");
      } else {
        message.error(updateData?.addUpdateSuperAdmins?.message || "Update failed");
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleImageChange = ({ file }) => {
    const actualFile = file?.originFileObj || file;
    if (!actualFile) return;
    if (!["image/jpeg", "image/png"].includes(actualFile.type)) {
      message.error("Invalid file format. Only JPEG and PNG are allowed.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setClientImage(e.target.result);
    reader.readAsDataURL(actualFile);
    setErrors((p) => ({ ...p, clientImage: "" }));

    uploadDocument({
      variables: { s3folder: "COMPANY_LOGO", document: actualFile },
      context: { useMultipart: true },
      onCompleted: (res) => {
        if (res?.uploadDocument?.success) {
          setUploadedFilename(res.uploadDocument?.data?.data?.filename);
          message.success("Image uploaded successfully!");
        } else {
          message.error("Failed to upload image: " + res?.uploadDocument?.message);
        }
      },
      onError: (err) => message.error("Upload failed: " + err.message),
    });
  };

  const onLogoClick = () => {
    if (!userData) return;
    const role = userData.role?.toLowerCase();
    if (role === "admin") navigate("/admin-dashboard");
    else if (role?.includes("clan crew") || role === "clancrew") navigate("/clan-crew-dashboard");
    else navigate("/dashboard");
  };

  const isClanCrew =
    userData?.role?.toLowerCase().includes("clan crew") ||
    userData?.role?.toLowerCase() === "clancrew";

  /* ---------------------------------- render ------------------------------- */
  return (
    <header className="fixed top-0 left-0 w-full h-[76px] bg-white flex items-center justify-between px-5 z-[3] shadow-[0_2px_10px_rgba(0,0,0,0.1)]">
      <img
        src={isClanCrew ? ClanCrewLogo : QPLogo}
        alt="Logo"
        className="h-[62px] cursor-pointer"
        onClick={onLogoClick}
      />

      {userData && (
        <div
          ref={dropdownRef}
          className={`relative w-[198px] border-[1.5px] rounded-[12px] transition-colors group ${
            isDropdownOpen
              ? "bg-[#AD0101] border-[#AD0101] rounded-b-none"
              : "bg-white border-[#940B0B] hover:bg-[#AD0101] hover:border-[#AD0101]"
          }`}
        >
          <button
            type="button"
            className="w-full h-[49px] px-2.5 grid grid-cols-[auto_1fr_auto] items-center gap-3"
            onClick={() => setIsDropdownOpen((p) => !p)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img src={getIcon()} alt="" className="h-6 w-6" />
            <span className="flex flex-col items-start gap-0.5 min-w-0">
              <span
                className={`text-[18px] font-bold font-[Inter] leading-tight group-hover:text-white ${
                  isDropdownOpen ? "text-white" : "text-[#AD0101]"
                }`}
              >
                {truncateUserName(userData.name)}
              </span>
              {/* <span
                className={`text-[11px] font-[Inter] leading-tight truncate w-full text-left group-hover:text-white ${
                  isDropdownOpen ? "text-white" : "text-[#1F2020]"
                }`}
                title={userData.role}
              >
                {truncateDesignation(userData.role)}
              </span> */}
            </span>
            <img
              src={userData.profilePicture || DefaultProfile}
              alt="User"
              className="h-[36px] w-[36px] rounded-full object-cover border-2 border-white"
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute top-full -left-[1.5px] -right-[1.5px] bg-[#AD0101] rounded-b-[12px] border-[1.5px] border-[#AD0101] border-t-0 px-3 py-2 z-10">
              <button
                type="button"
                onClick={handleEditClick}
                className="w-full flex items-center gap-3 py-2.5 text-white text-[13px] font-semibold font-[Inter]"
              >
                <img src={EditIcon} alt="" className="h-4 w-4 object-contain" />
                EDIT PROFILE
              </button>
              <button
                type="button"
                onClick={handleEditClient}
                className="w-full flex items-center gap-3 py-2.5 text-white text-[13px] font-semibold font-[Inter]"
              >
                <img src={EditIcon} alt="" className="h-4 w-4 object-contain" />
                EDIT CLIENT
              </button>
              <button
                type="button"
                onClick={logout}
                className="w-full flex items-center gap-3 py-2.5 text-white text-[13px] font-semibold font-[Inter]"
              >
                <img src={LogoutIcon} alt="" className="h-5 w-5 object-contain" />
                LOGOUT
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit Profile modal */}
      <Modal
        title="Edit Profile"
        open={isEditModalVisible}
        onCancel={handleCancel}
        footer={null}
        centered
        width={720}
      >
        <form onSubmit={handleSubmit} className="pt-2">
          <div className="flex items-start gap-6">
            <Upload
              name="clientImage"
              showUploadList={false}
              customRequest={handleImageChange}
              accept="image/jpeg,image/png"
            >
              <div className="h-[110px] w-[110px] rounded-full bg-[#F0F0F0] overflow-hidden flex items-center justify-center cursor-pointer">
                {clientImage || formData.imageUrl ? (
                  <img
                    ref={imageRef}
                    src={clientImage || formData.imageUrl}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img src={CreateClientImage} alt="Upload" className="h-8 w-8 object-contain" />
                )}
              </div>
            </Upload>
            {errors.clientImage && (
              <p className="text-[12px] text-[#AD0101] mt-2">{errors.clientImage}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-5">
            <div>
              <label className="block text-[13px] font-medium text-[#1F2020] mb-1.5">
                User's Name <span className="text-[#AD0101]">*</span>
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="Enter User's Name"
                className="w-full h-[42px] rounded-[6px] border border-[#D9DDE3] px-3 text-[14px] outline-none focus:border-[#AD0101]"
              />
              {errors.name && <p className="text-[12px] text-[#AD0101] mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1F2020] mb-1.5">
                Email ID <span className="text-[#AD0101]">*</span>
              </label>
              <input
                name="emailId"
                value={formData.emailId}
                onChange={(e) => setFormData((p) => ({ ...p, emailId: e.target.value }))}
                placeholder="Enter Email ID"
                className="w-full h-[42px] rounded-[6px] border border-[#D9DDE3] px-3 text-[14px] outline-none focus:border-[#AD0101]"
              />
              {errors.emailId && (
                <p className="text-[12px] text-[#AD0101] mt-1">{errors.emailId}</p>
              )}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1F2020] mb-1.5">
                Password <span className="text-[#AD0101]">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="password"
                  value={passwordVisible ? formData.password : maskedPassword}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((p) => ({ ...p, password: v }));
                    setMaskedPassword("*".repeat(v.length));
                  }}
                  placeholder="Enter Password"
                  className="w-full h-[42px] rounded-[6px] border border-[#D9DDE3] px-3 pr-10 text-[14px] outline-none focus:border-[#AD0101]"
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6A7282]"
                >
                  {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && (
                <p className="text-[12px] text-[#AD0101] mt-1">{errors.password}</p>
              )}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#1F2020] mb-1.5">
                Contact Number <span className="text-[#AD0101]">*</span>
              </label>
              <input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData((p) => ({ ...p, phoneNumber: e.target.value }))}
                placeholder="Enter Contact Number"
                className="w-full h-[42px] rounded-[6px] border border-[#D9DDE3] px-3 text-[14px] outline-none focus:border-[#AD0101]"
              />
              {errors.phoneNumber && (
                <p className="text-[12px] text-[#AD0101] mt-1">{errors.phoneNumber}</p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="h-11 px-6 rounded-[4px] border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#1F2020]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-11 px-6 rounded-[4px] bg-[#AD0101] hover:bg-[#8C0101] text-white text-[14px] font-medium transition"
            >
              Save
            </button>
          </div>
        </form>
      </Modal>
    </header>
  );
};

export default LiteGrowthHeader;
